import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import moment from "moment"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import { useTheme } from "context/AntdThemeContext"
import {
  getDefaultCalendarColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import styles from "./index.module.css"
import BookingConflictWarning from "pagesV2/pro/Calendar/Tooltips/BookingConflictWarning"
import BookingPopover from "pagesV2/pro/Calendar/shared/BookingPopover"
import WaiverBadge from "pagesV2/pro/Calendar/shared/WaiverBadge"
import { getWaiverStatus, getWaiverBadgeLabel, isWaiverAttached } from "pagesV2/pro/Calendar/shared/bookingTileUtils"

const BookingItem = ({ booking, conflict = false, setIsBookingModalOpen, setSelectedBookingId, compact = false }) => {
  const { start_time, end_time, uuid, client_name, color, multi_day } = booking || {}
  const showWaiverBadge = isWaiverAttached(booking)
  const { theme } = useTheme()
  const formattedTime = (t) => (t ? moment(t, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]).format("h:mm A") : "")
  let eventTime = formattedTime(start_time)
  if (end_time && !multi_day) {
    eventTime += (eventTime ? " - " : "") + formattedTime(end_time)
  }
  const primaryTextColor = getPrimaryCalendarTextColor(theme)
  const defaultColor = getDefaultCalendarColor()
  const previewColors = resolveBookingColors(color, defaultColor, primaryTextColor, getEventColor)

  const handleBookingClick = () => {
    setIsBookingModalOpen(true)
    setSelectedBookingId(uuid)
  }

  return (
    <BookingPopover booking={booking} colors={previewColors}>
      <Button
        type="link"
        className={`${compact ? styles.bookingBlockCompact : ""} ${styles.bookingBlock}`}
        style={{ backgroundColor: previewColors.backgroundColor, borderColor: previewColors.borderColor }}
        onClick={handleBookingClick}
      >
        {eventTime && (
          <div className={styles.timeRow}>
            <span
              className={compact ? styles.timeTextCompact : styles.timeText}
              style={{ color: previewColors.textColor || primaryTextColor }}
            >
              {eventTime}
            </span>
            {conflict ? <BookingConflictWarning /> : null}
          </div>
        )}
        <span
          className={compact ? styles.titleTextCompact : styles.titleText}
          style={{ color: previewColors.textColor || primaryTextColor }}
        >
          {client_name}
        </span>
        {showWaiverBadge && (
          <div className={styles.badgeRow}>
            <WaiverBadge
              status={getWaiverStatus(booking)}
              label={getWaiverBadgeLabel(booking)}
              colors={previewColors}
            />
          </div>
        )}
      </Button>
    </BookingPopover>
  )
}

BookingItem.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    payment_status: PropTypes.string,
    title: PropTypes.string.isRequired,
    formatted_start_time: PropTypes.string,
    formatted_end_time: PropTypes.string
  }).isRequired,
  conflict: PropTypes.bool,
  setIsBookingModalOpen: PropTypes.func,
  setSelectedBookingId: PropTypes.func,
  compact: PropTypes.bool,
}

export default memo(BookingItem)
